/**
 * Configuration manager for B2M Tracker
 */
export default class Config {
  constructor() {
    // Default configuration
    this.apiUrl = 'https://tracker.b2metric.com/tracker/tracker/b2m/collect'; // Default API endpoint
    this.experimentApiUrl = 'https://experiments-api.b2metric.com/Experiments/assign'; // A/B test assignment endpoint
    this.experimentCacheTtl = 24 * 60 * 60 * 1000; // 24h — refresh assignments periodically so winner switches propagate
    this.apiKey = null;

    // Session Replay configuration
    this.enableSessionReplay = false;        // opt-in — disabled by default
    this.sessionReplayScriptUrl = 'https://test.sr.b2metric.com/script.js';
    this.sessionReplayHost = 'https://api.test.sr.b2metric.com';
    this.sessionReplaySiteId = null;         // data-site-id value — numeric (must be provided)
    this.sessionReplayUseCustomSessionId = false; // when true, forward data-use-custom-session-id="true"
                                                  // and start SR with the Web SDK's own session ID
    this.debug = false;
    this.batchSize = 5;
    this.sendInterval = 30000; // 30 seconds default
    this.respectDoNotTrack = false; // Disabled by default (GA also ignores DNT)
    this.targetPlatforms = ["google", "meta", "tiktok", "b2m"];
    this.retryCount = 0;
    this.maxRetries = 3;
    this.retryTimeout = 30000;
    
    // Session configuration
    this.sessionTimeout = 30 * 60 * 1000;        // 30 min inactivity timeout
    this.maxSessionDuration = 24 * 60 * 60 * 1000; // 24 hr hard cap (PostHog best practice)
    
    // Bot filtering - filter out known bots and crawlers
    this.filterBots = true; // Enabled by default (GTM doesn't do this, giving us cleaner data)
    
    // Consent management (GDPR/KVKK)
    // When true, events are CAPTURED (queued) but NOT transmitted — not even on
    // page unload — until giveConsent() is called, which then flushes them.
    // revokeConsent() drops the captured-but-unsent queue. This is consent-aware
    // capture, NOT a consent bypass: the integrator (via their CMP) decides when
    // to call giveConsent(). Note attribution is still written to storage for
    // capture; classify those cookies in your CMP accordingly (or load the SDK
    // only after consent if your cookies aren't essential).
    // Script tag: data-require-consent="true"
    this.requireConsent = false; // Disabled by default for backward compatibility
    
    // Duplicate event prevention
    this.duplicatePreventionTimeout = 5000; // 5 seconds default
    
    // SPA duplicate prevention - shorter timeout for navigation events
    this.spaNavigationTimeout = 300; // 300ms for SPA navigations (hızlı geçişleri yakala)
    
    // SPA (Single Page Application) navigation tracking
    this.enableSPATracking = true; // Auto-track route changes in SPA frameworks
    this.spaPollingInterval = 100; // 100ms polling (daha hızlı algılama)
    
    // E-commerce platform detection
    this.enablePlatformDetection = true; // Auto-detect Ticimax, Shopify, WooCommerce, etc.
    this.forcePlatform = null; // Override auto-detection: 'ticimax', 'shopify', 'woocommerce', 'generic'
    
    // GTM DataLayer Integration
    this.enableGTMDataLayerListener = false; // Listen to Google Tag Manager dataLayer events
    this.gtmExcludeEvents = ['page_view']; // Events to exclude from GTM listener (B2M handles these)
    
    // Critical events that should be sent immediately (not batched)
    this.immediateEvents = ['page_view', 'session_start', 'session_end', 'purchase'];
    
    // iframe Mode Configuration
    // When SDK runs inside a 3rd-party iframe (e.g., Pulpoar widget embedded on other sites)
    this.iframeMode = false;          // Auto-detected or manually set
    this.iframeAutoDetect = true;     // Auto-detect if running inside an iframe

    // Countly Interceptor
    // Pulpoar gibi Countly kullanan widget'larda Countly.add_event / Countly.q.push çağrılarını
    // otomatik yakala ve B2M'e yönlendir. Pulpoar kodunu değiştirmeden geçişi sağlar.
    this.enableCountlyInterceptor = false;

    // Custom Event Library
    // Belirli bir 3rd-party widget/SDK'nın eventlerini otomatik toplamak için kullanılır.
    // Desteklenen değerler: 'pulpo'
    // Script tag'den okunur: data-custom-event-lib="pulpo"
    this.customEventLib = null;

    // App Identifier
    // user_properties.app_identifier alanına yazılır → event'lerin hangi web
    // projesinden/domain'den geldiğini ayrıştırmak için. null bırakılırsa SDK
    // çalıştığı tarayıcının hostname'ini (subdomain dahil) otomatik kullanır.
    // Script tag'den override edilir: data-app-identifier="b2metric.com"
    this.appIdentifier = null;

    // Cookie Domain (cross-subdomain attribution & session sharing)
    // Shares first-touch UTM, campaign params and the session across subdomains
    // — so a user who lands on birfatura.com and then registers on
    // uygulama.birfatura.com keeps the same session_id and utm_source instead of
    // starting fresh.
    //   'auto' (default) → auto-derive the registrable domain (e.g. ".birfatura.com")
    //   '.example.com'   → use this domain explicitly
    //   false/'off'/null → disable (host-only storage, no cross-subdomain sharing)
    // Script tag: data-cookie-domain="auto" | ".example.com" | "off"
    this.cookieDomain = 'auto';

    // A/B-only mode. When true, the SDK runs purely as an experiment-exposure
    // client: it stops sending behavioural events (page_view, session_start/end,
    // clicks, form_submit and the engagement group: scroll_depth, time_on_page…)
    // but KEEPS sending ab_test_view and user properties. This lets an A/B tool
    // still receive exposure signals while behavioural data already pulled from
    // BigQuery is not duplicated. Defaults to false for backward compatibility —
    // normal integrations keep collecting both core events and ab_test_view.
    this.abMode = false;

    // Event Groups Configuration
    this.eventGroups = this.getDefaultEventGroups();
  }
  
  getDefaultEventGroups() {
    return {
      // Core events (behavioural — turned off in A/B-only mode via abMode)
      core: {
        enabled: true,
        events: ['page_view', 'session_start', 'session_end', 'button_click', 'link_click', 'form_submit'],
        autoTrack: ['page_view', 'session_start', 'button_click', 'link_click', 'form_submit']
      },

      // Experiments — decoupled from core so A/B exposure (ab_test_view) keeps
      // firing even when behavioural core events are disabled (abMode).
      // Always enabled; not affected by abMode.
      experiments: {
        enabled: true,
        events: ['ab_test_view'],
        autoTrack: []
      },

      // E-commerce events
      ecommerce: {
        enabled: false,
        events: [
          'view_item', 'view_item_list', 'select_item', 
          'add_to_cart', 'remove_from_cart', 'view_cart',
          'begin_checkout', 'add_shipping_info', 'add_payment_info',
          'purchase', 'refund',
          'add_to_wishlist', 'remove_from_wishlist',
          'view_promotion', 'select_promotion',
          'coupon_apply'
        ],
        autoTrack: [
          'view_item',           // Ürün detay sayfası otomatik
          'view_item_list',      // Kategori/liste sayfaları otomatik
          'select_item',         // Ürün seçimi otomatik
          'add_to_cart',         // Sepete ekle buton otomatik
          'remove_from_cart',    // Sepetten çıkar buton otomatik
          'view_cart',           // Sepet sayfası otomatik
          'begin_checkout',      // Checkout sayfası otomatik
          'add_shipping_info',   // Kargo bilgisi ekleme otomatik
          'add_payment_info',    // Ödeme bilgisi ekleme otomatik
          'purchase',            // Satın alma otomatik (thank you page)
          'refund',              // İade otomatik
          'add_to_wishlist',     // Favorilere ekleme otomatik
          'remove_from_wishlist',// Favorilerden çıkarma otomatik
          'view_promotion',      // Promosyon görüntüleme otomatik
          'select_promotion',    // Promosyon seçimi otomatik
          'coupon_apply'         // Kupon uygulama otomatik
        ]
      },
      
      // Media and content
      media: {
        enabled: false,
        events: ['video_start', 'video_complete', 'video_pause', 'image_zoom', 'article_read', 'pdf_view', 'social_share'],
        autoTrack: ['video_start', 'video_complete', 'video_pause']
      },
      
      // User account operations
      user: {
        enabled: false,
        events: ['user_register', 'user_login', 'user_logout', 'profile_update', 'password_change', 'account_delete'],
        autoTrack: ['user_register', 'user_login', 'user_logout', 'profile_update', 'password_change', 'account_delete']
      },
      
      // Search and navigation
      search: {
        enabled: false,
        events: ['search', 'search_result_click', 'filter_apply', 'sort_change', 'category_view', 'pagination'],
        autoTrack: ['search']
      },
      
      // Performance and technical
      performance: {
        enabled: false,
        events: ['page_performance', 'javascript_error', 'api_timing', 'network_change', 'image_load_error'],
        autoTrack: ['page_performance', 'javascript_error']
      },
      
      // Engagement
      engagement: {
        enabled: true,
        events: ['scroll_depth', 'time_on_page', 'tab_change', 'modal_open', 'accordion_toggle', 'user_idle', 'user_active'],
        autoTrack: ['scroll_depth', 'time_on_page', 'tab_change', 'modal_open', 'accordion_toggle', 'user_idle', 'user_active']
      },
      
      // Communication
      communication: {
        enabled: false,
        events: ['chat_start', 'phone_click', 'email_send', 'contact_form', 'faq_view', 'review_submit'],
        autoTrack: ['phone_click', 'contact_form']
      },
      
      // Marketing
      marketing: {
        enabled: false,
        events: ['banner_click', 'newsletter_signup', 'promotion_view', 'popup_view'],
        autoTrack: ['banner_click', 'newsletter_signup', 'popup_view']
      },
      
      // Mobile specific
      mobile: {
        enabled: false,
        events: ['app_download_click', 'qr_code_scan', 'location_used', 'orientation_change', 'battery_low'],
        autoTrack: ['app_download_click', 'orientation_change']
      }
    };
  }
  
  init(userConfig) {
    // Merge user configuration with defaults
    Object.assign(this, userConfig);
    
    // Auto-detect iframe mode if enabled
    if (this.iframeAutoDetect && !userConfig.hasOwnProperty('iframeMode')) {
      try {
        this.iframeMode = window.self !== window.top;
      } catch (e) {
        // Cross-origin: accessing window.top throws SecurityError → we are in an iframe
        this.iframeMode = true;
      }
    }
    
    // Apply iframe-optimized defaults when in iframe mode
    if (this.iframeMode) {
      // Use smaller batch size (iframe can close anytime)
      if (!userConfig.hasOwnProperty('batchSize')) {
        this.batchSize = 1;
      }
      // Shorter send interval
      if (!userConfig.hasOwnProperty('sendInterval')) {
        this.sendInterval = 5000;
      }
      // Disable SPA tracking (not needed in iframe widgets)
      if (!userConfig.hasOwnProperty('enableSPATracking')) {
        this.enableSPATracking = false;
      }
      // Disable platform detection (iframe doesn't have access to parent DOM)
      if (!userConfig.hasOwnProperty('enablePlatformDetection')) {
        this.enablePlatformDetection = false;
      }
      
      if (this.debug) {
        console.log('[B2M SDK] iframe mode detected - using optimized settings:', {
          batchSize: this.batchSize,
          sendInterval: this.sendInterval,
          enableSPATracking: this.enableSPATracking,
          enablePlatformDetection: this.enablePlatformDetection
        });
      }
    }
    
    // Merge event groups if provided
    if (userConfig.eventGroups) {
      if (this.debug) console.log('Before merge:', JSON.stringify(this.eventGroups, null, 2));
      this.eventGroups = this.mergeEventGroups(this.eventGroups, userConfig.eventGroups);
      if (this.debug) console.log('After merge:', JSON.stringify(this.eventGroups, null, 2));
    }
    
    // Validate event groups structure
    this.validateEventGroups();
    if (this.debug) console.log('After validation:', JSON.stringify(this.eventGroups, null, 2));

    // Apply A/B-only mode AFTER validation so it can't be reverted by the
    // default-restore logic in validateEventGroups(). Turns off behavioural data
    // (core + engagement) while leaving the experiments group active, so
    // ab_test_view and user properties keep flowing.
    if (this.abMode === true) {
      if (this.eventGroups.core)       this.eventGroups.core.enabled = false;
      if (this.eventGroups.engagement) this.eventGroups.engagement.enabled = false;
      if (this.debug) console.log('[B2M SDK] abMode=true → core & engagement disabled; experiments (ab_test_view) still active');
    }
    
    // Set default customApiUrl if not provided but apiUrl exists
    if (!this.customApiUrl && this.apiUrl) {
      this.customApiUrl = 'https://tracker.b2metric.com/tracker/tracker/b2m/custom';
    }
    
    return this;
  }
  
  validateEventGroups() {
    if (!this.eventGroups || typeof this.eventGroups !== 'object') {
      console.warn('Invalid eventGroups configuration, using defaults');
      this.eventGroups = this.getDefaultEventGroups();
      return;
    }
    
    const defaultGroups = this.getDefaultEventGroups();
    if (this.debug) console.log('Default groups for validation:', JSON.stringify(defaultGroups, null, 2));
    
    // Ensure each group has required properties
    Object.keys(this.eventGroups).forEach(groupName => {
      const group = this.eventGroups[groupName];
      if (this.debug) console.log(`Validating group '${groupName}':`, JSON.stringify(group, null, 2));
      
      if (!group || typeof group !== 'object') {
        console.warn(`Invalid event group '${groupName}', resetting to default`);
        this.eventGroups[groupName] = defaultGroups[groupName] || {
          enabled: false,
          events: [],
          autoTrack: []
        };
        return;
      }
      
      // Ensure required properties exist
      if (typeof group.enabled !== 'boolean') {
        group.enabled = false;
      }
      
      if (!Array.isArray(group.events)) {
        if (this.debug) console.log(`Group '${groupName}' events is not array, fixing...`);
        // If group exists in defaults, use default events
        if (defaultGroups[groupName] && Array.isArray(defaultGroups[groupName].events)) {
          group.events = [...defaultGroups[groupName].events];
          if (this.debug) console.log(`Set default events for '${groupName}':`, group.events);
        } else {
          group.events = [];
        }
      } else if (group.events.length === 0 && defaultGroups[groupName] && defaultGroups[groupName].events.length > 0) {
        // If events array is empty but defaults exist, restore defaults
        group.events = [...defaultGroups[groupName].events];
        if (this.debug) console.log(`Restored default events for '${groupName}':`, group.events);
      }
      
      if (!Array.isArray(group.autoTrack)) {
        if (this.debug) console.log(`Group '${groupName}' autoTrack is not array, fixing...`);
        // If group exists in defaults, use default autoTrack
        if (defaultGroups[groupName] && Array.isArray(defaultGroups[groupName].autoTrack)) {
          group.autoTrack = [...defaultGroups[groupName].autoTrack];
          if (this.debug) console.log(`Set default autoTrack for '${groupName}':`, group.autoTrack);
        } else {
          group.autoTrack = [];
        }
      } else if (group.autoTrack.length === 0 && defaultGroups[groupName] && defaultGroups[groupName].autoTrack.length > 0) {
        // If autoTrack array is empty but defaults exist, restore defaults
        group.autoTrack = [...defaultGroups[groupName].autoTrack];
        if (this.debug) console.log(`Restored default autoTrack for '${groupName}':`, group.autoTrack);
      }
    });
  }
  
  mergeEventGroups(defaultGroups, userGroups) {
    const merged = { ...defaultGroups };
    
    Object.keys(userGroups).forEach(groupName => {
      if (merged[groupName]) {
        // Store default arrays before merging - with safety checks
        const defaultEvents = Array.isArray(merged[groupName].events) ? [...merged[groupName].events] : [];
        const defaultAutoTrack = Array.isArray(merged[groupName].autoTrack) ? [...merged[groupName].autoTrack] : [];
        
        // Merge existing group
        merged[groupName] = {
          ...merged[groupName],
          ...userGroups[groupName]
        };
        
        // If user didn't provide events/autoTrack arrays, restore defaults
        if (!userGroups[groupName].hasOwnProperty('events')) {
          merged[groupName].events = defaultEvents;
        } else if (Array.isArray(userGroups[groupName].events)) {
          // If user provided events, merge with defaults
          merged[groupName].events = [
            ...new Set([...defaultEvents, ...userGroups[groupName].events])
          ];
        } else {
          // If user provided non-array events, use defaults
          merged[groupName].events = defaultEvents;
        }
        
        if (!userGroups[groupName].hasOwnProperty('autoTrack')) {
          merged[groupName].autoTrack = defaultAutoTrack;
        } else if (Array.isArray(userGroups[groupName].autoTrack)) {
          // If user provided autoTrack, merge with defaults
          merged[groupName].autoTrack = [
            ...new Set([...defaultAutoTrack, ...userGroups[groupName].autoTrack])
          ];
        } else {
          // If user provided non-array autoTrack, use defaults
          merged[groupName].autoTrack = defaultAutoTrack;
        }
      } else {
        // Add new group - make sure it has required properties
        merged[groupName] = {
          enabled: false,
          events: [],
          autoTrack: [],
          ...userGroups[groupName]
        };
        
        // Ensure arrays are actually arrays
        if (!Array.isArray(merged[groupName].events)) {
          merged[groupName].events = [];
        }
        if (!Array.isArray(merged[groupName].autoTrack)) {
          merged[groupName].autoTrack = [];
        }
      }
    });
    
    return merged;
  }
  
  isEventEnabled(eventName) {
    for (const [groupName, group] of Object.entries(this.eventGroups)) {
      if (group.enabled && group.events && Array.isArray(group.events) && group.events.includes(eventName)) {
        return true;
      }
    }
    return false;
  }
  
  isAutoTrackEnabled(eventName) {
    for (const [groupName, group] of Object.entries(this.eventGroups)) {
      if (group.enabled && group.autoTrack && Array.isArray(group.autoTrack) && group.autoTrack.includes(eventName)) {
        return true;
      }
    }
    return false;
  }
  
  getEnabledEvents() {
    const enabledEvents = [];
    Object.values(this.eventGroups).forEach(group => {
      if (group.enabled && group.events && Array.isArray(group.events)) {
        enabledEvents.push(...group.events);
      }
    });
    return [...new Set(enabledEvents)];
  }
  
  getAutoTrackEvents() {
    const autoTrackEvents = [];
    Object.values(this.eventGroups).forEach(group => {
      if (group.enabled && group.autoTrack && Array.isArray(group.autoTrack)) {
        autoTrackEvents.push(...group.autoTrack);
      }
    });
    return [...new Set(autoTrackEvents)];
  }
  
  getEventGroup(eventName) {
    for (const [groupName, group] of Object.entries(this.eventGroups)) {
      if (group.events && Array.isArray(group.events) && group.events.includes(eventName)) {
        return groupName;
      }
    }
    return null;
  }
  
  enableEventGroup(groupName) {
    if (this.eventGroups[groupName]) {
      this.eventGroups[groupName].enabled = true;
      return true;
    }
    return false;
  }
  
  disableEventGroup(groupName) {
    if (this.eventGroups[groupName] && groupName !== 'core') {
      this.eventGroups[groupName].enabled = false;
      return true;
    }
    return false;
  }
  
  validate() {
    // Check required fields
    // Accept either 'apiKey' or 'token' (token is an alias)
    if (this.token && !this.apiKey) {
      this.apiKey = this.token;
    }
    
    return (
      this.apiUrl && 
      this.apiKey
    );
  }
  
  getAll() {
    return { ...this };
  }
} 